import { addClass, removeClass } from 'ally.js/util/toggle-class';
import shadowFocus from 'ally.js/event/shadow-focus';
import engageInteractionTypeObserver from 'ally.js/observe/interaction-type';
// preferring focusin/out because they are synchronous in IE10+11
const supportsFocusIn = typeof document !== 'undefined' && 'onfocusin' in document;
const focusEventName = supportsFocusIn ? 'focusin' : 'focus';
const blurEventName = supportsFocusIn ? 'focusout' : 'blur';
// interface to read interaction-type-listener state
let interactionTypeHandler;
let shadowHandle;
// keep track of last focus source
let current = null;
// overwrite focus source for use with the every upcoming focus event
let lock = null;
// keep track of ever having used a particular input method to change focus
const used = {
    pointer: false,
    key: false,
    script: false,
    initial: false,
};
function handleFocusEvent(target = document.documentElement, event) {
    let source = '';
    if (event.type === focusEventName || event.type === 'shadow-focus') {
        const interactionType = interactionTypeHandler.get();
        source =
            lock ||
                (interactionType.pointer && 'pointer') ||
                (interactionType.key && 'key') ||
                'script';
    }
    else if (event.type === 'initial') {
        source = 'initial';
    }
    target.setAttribute('data-focus-source', source);
    if (event.type !== blurEventName) {
        if (!used[source]) {
            addClass(target, 'focus-source-' + source);
        }
        used[source] = true;
        current = source;
    }
}
function getCurrentFocusSource() {
    return current;
}
function getUsedFocusSource(source) {
    return used[source];
}
function lockFocusSource(source) {
    lock = source;
}
function unlockFocusSource() {
    lock = false;
}
function disengage(target) {
    const handleFocusEventWithTarget = ((el) => (event) => handleFocusEvent(el, event))(target);
    // clear dom state
    handleFocusEventWithTarget({ type: blurEventName });
    current = lock = null;
    Object.keys(used).forEach(function (key) {
        removeClass(target, 'focus-source-' + key);
        used[key] = false;
    });
    // kill interaction type identification listener
    interactionTypeHandler && interactionTypeHandler.disengage();
    // kill shadow-focus event dispatcher
    shadowHandle && shadowHandle.disengage();
    document.removeEventListener('shadow-focus', handleFocusEventWithTarget, true);
    target.removeEventListener(focusEventName, handleFocusEventWithTarget, true);
    target.removeEventListener(blurEventName, handleFocusEventWithTarget, true);
    target.removeAttribute('data-focus-source');
}
function engage(target) {
    const handleFocusEventWithTarget = ((el) => (event) => handleFocusEvent(el, event))(target);
    // enable the shadow-focus event dispatcher
    shadowHandle = shadowFocus();
    // handlers to modify the focused element
    document.addEventListener('shadow-focus', handleFocusEventWithTarget, true);
    target.addEventListener(focusEventName, handleFocusEventWithTarget, true);
    target.addEventListener(blurEventName, handleFocusEventWithTarget, true);
    // enable the interaction type identification observer
    interactionTypeHandler = engageInteractionTypeObserver();
    // set up initial dom state
    handleFocusEventWithTarget({ type: 'initial' });
    return {
        used: getUsedFocusSource,
        current: getCurrentFocusSource,
        lock: lockFocusSource,
        unlock: unlockFocusSource,
    };
}
export const focusSource = { engage, disengage };
//# sourceMappingURL=focus-source.js.map